import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock, RefreshCw, Sparkles, Users } from "lucide-react";
import { Modal, VbButton, VbEmpty, VbError, CompetitorFavicon, Callout } from "@/components/vb";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useCompanyBuyerCap, useEnsureCompanyContext } from "@/hooks/vb/useBuyers";
import { PersonaStep } from "./PersonaStep";
import { QuestionsStep } from "./QuestionsStep";
import { PickerSkeleton } from "./PickerSkeleton";
import { extractVbError } from "./errors";
import { useBuyerWizard, wizardIsCapBlocked } from "./useBuyerWizard";

/** Shared 3-step progress header, reused by the loading / error / ready states
 *  so the chrome is identical as the wizard resolves its company context. */
function WizardSteps() {
  return (
    <div className="steps">
      <span className="step active">
        <span className="num">1</span>
        Set up buyer &amp; questions
      </span>
      <span className="sep" />
      <span className="step upcoming">
        <span className="num">2</span>
        Run simulation
      </span>
      <span className="sep" />
      <span className="step upcoming">
        <span className="num">3</span>
        Review verdict
      </span>
    </div>
  );
}

/** Create-buyer page (VB-B1 persona + VB-B2 questions on one page, matching the
 *  aits-2.0 prototype). Entered scoped to a pre-selected company via
 *  `?company=<id>`. The persona form is always editable; the questions section
 *  below is gated until the persona is complete. */
export function BuyerCreateWizard() {
  const [params] = useSearchParams();
  const raw = params.get("company");
  const companyId = raw && /^\d+$/.test(raw) ? Number(raw) : null;

  // Resolve the pre-selected company's name + url (for the header, and to stamp
  // virtual_buyer_created) from the workspace the user is in — the wizard enters
  // scoped to `?company=<id>`.
  const { workspaces } = useWorkspace();
  const company = (() => {
    if (companyId == null) return null;
    for (const ws of workspaces) {
      const c = ws.companies.find((x) => x.company_id === companyId);
      if (c) return c;
    }
    return null;
  })();

  // Hooks must run unconditionally; the wizard is inert until the user acts.
  const w = useBuyerWizard(companyId ?? 0, undefined, company?.company_name ?? null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Ensure the company has a context bundle before the wizard is usable. This
  // is what makes a never-analyzed company (e.g. a v1 company added via a
  // report, opening the buyer wizard for the first time) work: we scrape it on
  // entry behind a loading state instead of dead-ending on Suggest/Generate.
  const ctx = useEnsureCompanyContext(companyId ?? undefined);
  const companyName = company?.company_name?.trim() || "your company";
  const companyUrl = company?.company_url ?? "";

  // The overview disables "New buyer" at the cap, but this page is reachable
  // directly (a bookmark, a back-nav, onboarding's resume link), so it has to
  // gate too — and up front. The create only happens at "Generate questions",
  // so without this the user writes a whole persona and loses it to a 400.
  const cap = useCompanyBuyerCap(companyId ?? undefined);

  // Cancel asks first when there's work to lose; a pristine wizard just leaves.
  const requestCancel = () => {
    if (w.finishing) return;
    if (w.hasWork) setConfirmCancel(true);
    else void w.cancel();
  };

  // Regenerate, protecting any edited/manual questions behind a confirm. Shared
  // by the questions-section "Regenerate all" button and the stale-buyer nudge.
  const requestRegenerate = () => {
    if (w.hasEdits) setConfirmRegen(true);
    else void w.generateQuestions();
  };

  if (companyId == null) {
    return (
      <>
        <VbError
          title="No company selected"
          description="Open the buyer wizard from a company so we know who the buyer is evaluating."
        />
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Link to="/virtual-buyer" className="vb-link">
            ← Back to all buyers
          </Link>
        </div>
      </>
    );
  }

  // This company already holds its allowance, so there is nothing to START here.
  // Checked before the context-loading branch below so a full company doesn't
  // sit on a spinner it can never leave usefully.
  if (wizardIsCapBlocked({ atCap: cap.atCap, hasWork: w.hasWork })) {
    const label = company?.company_name?.trim() || "This company";
    return (
      <div className="vb-wizard">
        <WizardSteps />
        <VbEmpty
          icon={<Users className="h-5 w-5" />}
          title={
            cap.used > cap.max
              ? `${label} has ${cap.used} virtual buyers`
              : `${label} has all ${cap.max} virtual buyers`
          }
          // `mustDelete`, not "one": above the cap, deleting one still leaves
          // them over it. Matches the backend's 400 copy.
          description={
            `${cap.max} is the most we allow for one company. Delete ` +
            `${cap.mustDelete === 1 ? "one of its buyers" : `${cap.mustDelete} of its buyers`} ` +
            `to make room, or switch companies in the sidebar to add a buyer there.`
          }
          action={
            // A Link wearing `.vb-btn` (as VerdictPage does), not a VbButton
            // inside a Link — that nests <button> in <a>, which is invalid HTML
            // and loses middle-click / open-in-new-tab. `.vb-btn` is unscoped
            // and sets text-decoration:none precisely so an anchor can wear it;
            // bare `.vb-btn` IS the secondary style.
            <Link className="vb-btn" to={`/virtual-buyer?company=${companyId}`}>
              Back to all buyers
            </Link>
          }
        />
      </div>
    );
  }

  // Company context is still being ensured (or the auth token hasn't resolved
  // yet) → a layout-matching loading state so the user never sees a half-built
  // wizard that can't actually suggest or generate.
  //
  // `!cap.known` joins that gate deliberately. The dead end above is vetoed by
  // `hasWork`, so if the form rendered while the count was still loading, one
  // keystroke would set `hasWork` for the rest of the mount and a full company
  // could never show it. Waiting costs nothing: the cap query runs in parallel
  // with the context ensure, and is the faster of the two.
  if ((!ctx.isSuccess && !ctx.isError) || !cap.known) {
    return (
      <div className="vb-wizard">
        <WizardSteps />
        <div className="persona-hd">
          <div className="left">
            <span className="tag">Buyer</span>
            <h3 className="persona-hd__title">
              Setting up your buyer for{" "}
              {companyUrl ? (
                <span className="persona-hd__co">
                  <CompetitorFavicon name={companyName} url={companyUrl} size={18} />
                  <span className="persona-hd__co-name">{companyName}</span>
                </span>
              ) : (
                companyName
              )}
            </h3>
            <div className="desc vb-context-loading__line">
              <Sparkles size={14} aria-hidden="true" />
              Reading {companyName}&apos;s website so the questions fit this buyer. This takes a few
              seconds the first time.
            </div>
          </div>
        </div>
        <PickerSkeleton rails={4} lines={3} />
      </div>
    );
  }

  // Context couldn't be built (bad/empty site, upstream hiccup) → an actionable
  // error with retry, instead of letting the user fail later on Suggest.
  if (ctx.isError) {
    const e = extractVbError(ctx.error);
    return (
      <div className="vb-wizard">
        <WizardSteps />
        <VbError
          title={`We couldn't set up ${companyName}`}
          description={
            e.code === "CONTEXT_MISSING" || e.code === "CANNOT_INFER"
              ? `We couldn't read ${companyName}'s website to tailor this buyer. Check the company's URL is reachable, then try again.`
              : e.detail
          }
          onRetry={() => void ctx.refetch()}
        />
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Link to="/virtual-buyer" className="vb-link">
            ← Back to all buyers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="vb-wizard">
      {/* 3-step progress - step 1 (this page) covers persona + questions. */}
      <WizardSteps />

      {/* Both sections on one page: persona on top, questions gated below. */}
      <PersonaStep w={w} companyName={companyName === "your company" ? "" : companyName} companyUrl={companyUrl} />

      {/* Nudge: the persona changed after the questions were generated, so they
          may no longer match. Offered right below the buyer form, where the edit
          happened. */}
      {w.questionsStale && w.hasQuestions && (
        <Callout
          className="mb-5"
          tone="warn"
          role="status"
          icon={<RefreshCw size={15} />}
          onDismiss={w.dismissStale}
        >
          You&apos;ve changed the buyer. Use <strong>Regenerate&nbsp;all</strong> to refresh the
          questions for the new persona.
        </Callout>
      )}

      <div className="vb-section-gap" />
      <QuestionsStep w={w} onRegenerate={requestRegenerate} />

      {w.doneError && (
        <div className="limit-flash show" role="alert" style={{ marginBottom: 12 }}>
          {w.doneError}
        </div>
      )}

      {/* Single sticky footer - anchored to the wizard, not the sidebar width. */}
      <div className="footer">
        <div className="summary">
          {w.hasQuestions ? (
            <>
              <div className="ct tnum">
                {w.questionCount} questions <span className="of">· max 10</span>
              </div>
              <div className="meta">
                <span className="mt">
                  <Clock size={13} aria-hidden="true" /> About 90 seconds to run
                </span>
                <span className="mt">·</span>
                <span className="mt">3 AI models: ChatGPT, Gemini, Perplexity</span>
              </div>
            </>
          ) : (
            <div className="meta">
              <span className="mt">Set up the buyer, then generate the questions to run.</span>
            </div>
          )}
        </div>

        <div className="right">
          <VbButton variant="ghost" onClick={requestCancel} disabled={w.finishing}>
            Cancel
          </VbButton>
          <VbButton
            onClick={() => void w.done()}
            disabled={!w.hasQuestions || w.finishing}
          >
            {w.finishing ? "Starting…" : "Run simulation"}
            <ArrowRight size={16} aria-hidden="true" />
          </VbButton>
        </div>
      </div>

      <Modal
        open={confirmRegen}
        onOpenChange={setConfirmRegen}
        title="Regenerate all questions?"
        description="This replaces the whole set with a fresh batch. Your edited and manually-added questions will be lost."
        footer={
          <>
            <VbButton variant="secondary" onClick={() => setConfirmRegen(false)}>
              Keep current
            </VbButton>
            <VbButton
              variant="danger"
              onClick={() => {
                setConfirmRegen(false);
                void w.generateQuestions();
              }}
            >
              Regenerate
            </VbButton>
          </>
        }
      />

      <Modal
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Discard this buyer?"
        description="You'll lose the buyer details and any questions you've set up. This can't be undone."
        footer={
          <>
            <VbButton variant="secondary" onClick={() => setConfirmCancel(false)}>
              Keep editing
            </VbButton>
            <VbButton
              variant="danger"
              onClick={() => {
                setConfirmCancel(false);
                void w.cancel();
              }}
            >
              Discard
            </VbButton>
          </>
        }
      />
    </div>
  );
}

export default BuyerCreateWizard;
